import React, { useEffect, useState, useMemo, useCallback } from "react";
import { AiFillHeart, AiOutlineShoppingCart } from "react-icons/ai";
import { FaEye } from "react-icons/fa";
import { motion, useAnimation } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { AdvancedImage } from "@cloudinary/react";
import { CloudinaryImage } from "@cloudinary/url-gen";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { format, quality } from "@cloudinary/url-gen/actions/delivery";
import toast from "react-hot-toast";
import Ratings from "../Ratings";
import {
  add_to_card,
  messageClear,
  add_to_wishlist,
} from "../../store/reducers/cardReducer";
import { autoGravity } from "@cloudinary/url-gen/qualifiers/gravity";

const ProductCard = React.memo(
  ({
    product,
    index,
    hoveredCard,
    onMouseEnter,
    onMouseLeave,
    addCard,
    addWishlist,
  }) => {
    const controls = useAnimation();

    useEffect(() => {
      controls.start({
        opacity: hoveredCard === index ? 1 : 0.9,
        scale: hoveredCard === index ? 1.02 : 1,
        transition: { duration: 0.3 },
      });
    }, [controls, hoveredCard, index]);

    const selectedImage =
      hoveredCard === index ? product.images[1] : product.images[0];

    // Extract the public ID from the full Cloudinary URL
    const publicId = selectedImage
      .split("/")
      .slice(-2)
      .join("/")
      .replace(/\.[^.]+$/, "");

    // Create a CloudinaryImage instance
    const cldImage = new CloudinaryImage(publicId, { cloudName: "dapninft5" })
      .delivery(format("auto")) // Automatically select best format (WebP, AVIF, etc.)
      .delivery(quality("auto:low")) // Auto optimize quality (reduces file size significantly)
      .resize(fill().width(400).height(240).gravity(autoGravity())); // Resize with auto crop

    return (
      <motion.div
        key={index}
        className="border group transition-all duration-300 hover:bg-white rounded-[20px] hover:shadow-md  hover:-mt-3"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        initial={{ opacity: 0.9, scale: 1 }}
        animate={controls}
      >
        <div className="relative transition-opacity duration-500 ease-in-out overflow-hidden">
          {product.discount ? (
            <div className="flex justify-center items-center absolute text-white w-[38px] h-[38px] rounded-full bg-red-500 font-semibold text-xs left-2 top-2">
              -{product.discount}%
            </div>
          ) : (
            ""
          )}
          {/* <img
            className="sm:w-full w-full rounded-[20px] h-[240px] object-cover"
            src={hoveredCard === index ? product.images[1] : product.images[0]}
            alt="product"
            loading="lazy"
          /> */}
          <AdvancedImage
            className="sm:w-full w-full rounded-[20px] h-[240px] object-cover"
            cldImg={cldImage}
          />

          <ul className="flex transition-all duration-700 -bottom-10 justify-center items-center gap-2 absolute w-full group-hover:bottom-3">
            <li
              onClick={() => addWishlist(product._id)}
              className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-secondary hover:text-white hover:rotate-[720deg] transition-all"
            >
              <AiFillHeart />
            </li>
            <Link
              to={`/product/details/${product.slug}`}
              className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-secondary hover:text-white hover:rotate-[720deg] transition-all"
            >
              <FaEye />
            </Link>
            <li
              onClick={() => addCard(product._id)}
              className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-secondary hover:text-white hover:rotate-[720deg] transition-all"
            >
              <AiOutlineShoppingCart />
            </li>
          </ul>
        </div>
        <div className="py-3 text-slate-600 px-2">
          <h2>{product.name}</h2>
          <div className="flex justify-start items-center gap-3">
            {product.discount !== 0 ? (
              <>
                <p className="line-through">Rs.{product.price}</p>
                <p>
                  Rs.
                  {product.price -
                    Math.floor((product.price * product.discount) / 100)}{" "}
                </p>
              </>
            ) : (
              <p>Price: Rs.{product.price}</p>
            )}
            <div className="flex">
              <Ratings ratings={product.rating} />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }
);

const FeatureProducts = ({ products }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { successMessage, errorMessage } = useSelector((state) => state.card);

  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [errorMessage, dispatch, successMessage]);

  const add_card = useCallback(
    (id) => {
      if (userInfo) {
        dispatch(
          add_to_card({
            userId: userInfo.id,
            quantity: 1,
            productId: id,
          })
        );
      } else {
        navigate("/login");
      }
    },
    [dispatch, navigate, userInfo]
  );

  const memoizedProductCards = useMemo(() => {
    const add_wishlist = (pro) => {
      if (userInfo) {
        dispatch(
          add_to_wishlist({
            userId: userInfo.id,
            productId: pro._id,
            name: pro.name,
            price: pro.price,
            image: pro.images[0],
            discount: pro.discount,
            rating: pro.rating,
            slug: pro.slug,
          })
        );
      } else {
        navigate("/login");
      }
    };

    return products.map((p, i) => (
      <ProductCard
        key={i}
        product={p}
        index={i}
        hoveredCard={hoveredCard}
        onMouseEnter={() => setHoveredCard(i)}
        onMouseLeave={() => setHoveredCard(null)}
        addCard={() => add_card(p._id)}
        addWishlist={() => add_wishlist(p)}
      />
    ));
  }, [products, hoveredCard, add_card, dispatch, navigate, userInfo]);

  return (
    <div className="w-[95%] flex flex-wrap mx-auto">
      <div className="w-full">
        <div className="text-center flex justify-center items-center flex-col text-4xl md:text-3xl text-[#212730] font-bold relative pb-[45px]">
          <h2>Feature Products</h2>
          <div className="w-[200px] h-[4px] bg-secondary mt-4"></div>
        </div>
      </div>
      <div className="w-full  grid grid-cols-4 md-lg:grid-cols-3 md:grid-cols-2  sm:grid-cols-1 gap-6">
        {memoizedProductCards}
      </div>
    </div>
  );
};

export default FeatureProducts;
